import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Drawer,
  Form,
  Switch,
  List,
  message as antMsg,
  Row,
  Col,
  Button,
  Tabs,
  Spin,
  Input,
  Tooltip
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import useGenerateFormItem from '../../../utils/GenerateFormItem';
import useFields from './fields';
import useAuthContext from '../../../contexts/AuthContext';
import useErrorMessage from '../../../utils/ErrorMessage';

const { TabPane } = Tabs;

const NewUserProfileDrawer = ({
  isDrawerVisible,
  setIsDrawerVisible,
  setForceRefresh,
  forceRefresh,
  recordToUpdate,
  setRecordToUpdate,
  insurer
}) => {
  const { t } = useTranslation();
  const { user, dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const generateFields = useGenerateFormItem();
  const [saveLoading, setSaveLoading] = useState(false);
  const [form] = Form.useForm();
  const {
    getFields,
    getControlled_fit_fields,
    getBlocked_fit_fields,
    getBlocked_fit_fields_prev,
    getControlled_fit_fields_prev
  } = useFields();
  const [otherPositionSelected, setOtherPositionSelected] = useState();
  const [products, setProducts] = useState([]);

  const menuPermissions = [
    [
      'dashboard',
      'remuneration',
      'accounting',
      'sinister',
      'commercial_messages'
    ],
    ['quotations', 'contracts']
  ];

  const getProducts = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/products' });
      setProducts(data);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      await getProducts();
    })();
    // eslint-disable-next-line
  }, [isDrawerVisible]);

  const onClose = () => {
    setIsDrawerVisible(!isDrawerVisible);
    setRecordToUpdate(undefined);
  };

  const handleMenuPopTabChange = (key, index) => {
    form.setFieldsValue({
      permissionManagement: {
        products_access: form
          .getFieldValue(['permissionManagement', 'products_access'])
          .map((p, idx) => {
            if (idx === index) {
              if (p.product.type === 'PREVOYANCE') {
                return {
                  ...p,
                  advancedManagement: {
                    full_fit: {
                      ...p.advancedManagement.full_fit,
                      selected: false
                    },
                    controlled_fit: {
                      ...p.advancedManagement.controlled_fit_prev,
                      selected: false
                    },
                    blocked_fit: {
                      ...p.advancedManagement.blocked_fit_prev,
                      selected: false
                    },
                    [key]: { ...p.advancedManagement[key], selected: true }
                  }
                };
              }
              if (p.product.type !== 'PREVOYANCE') {
                return {
                  ...p,
                  advancedManagement: {
                    full_fit: {
                      ...p.advancedManagement.full_fit,
                      selected: false
                    },
                    controlled_fit: {
                      ...p.advancedManagement.controlled_fit,
                      selected: false
                    },
                    blocked_fit: {
                      ...p.advancedManagement.blocked_fit,
                      selected: false
                    },
                    [key]: { ...p.advancedManagement[key], selected: true }
                  }
                };
              }
            }
            return p;
          })
      }
    });
  };

  const advancedManagement = (index) => {
    if (index === 3) {
      return (
        <Row>
          <Tabs
            style={{ width: 530 }}
            defaultActiveKey={() => {
              let key = 'full_fit';
              if (
                recordToUpdate?.permissionManagement?.products_access[index]
              ) {
                const management =
                  recordToUpdate?.permissionManagement?.products_access[index]
                    ?.advancedManagement;
                if (management) {
                  if (management.blocked_fit.selected) key = 'blocked_fit_prev';
                  if (management.controlled_fit.selected)
                    key = 'controlled_fit_prev';
                }
              }
              return key;
            }}
            onChange={(activeKey) => handleMenuPopTabChange(activeKey, index)}
            destroyInactiveTabPane
          >
            <TabPane
              tab={
                <span>
                  {t('settings.team.full_fit')}
                  <Tooltip title={t('settings.team.tooltip.tooltip_1')}>
                    <InfoCircleOutlined
                      style={{ marginLeft: 6, fontSize: 16 }}
                    />
                  </Tooltip>
                </span>
              }
              key="full_fit"
            >
              {t('settings.team.no_config')}
            </TabPane>
            <TabPane
              tab={
                <span>
                  {t('settings.team.blocked_fit')}
                  <Tooltip title={t('settings.team.tooltip.tooltip_2')}>
                    <InfoCircleOutlined
                      style={{ marginLeft: 6, fontSize: 16 }}
                    />
                  </Tooltip>
                </span>
              }
              key="blocked_fit_prev"
            >
              <Form.Item noStyle>
                <Input.Group>
                  {getBlocked_fit_fields_prev(index).map((field) =>
                    generateFields('settings.team', field)
                  )}
                </Input.Group>
              </Form.Item>
            </TabPane>
            <TabPane
              tab={
                <span>
                  {t('settings.team.controlled_fit')}
                  <Tooltip title={t('settings.team.tooltip.tooltip_3')}>
                    <InfoCircleOutlined
                      style={{ marginLeft: 6, fontSize: 16 }}
                    />
                  </Tooltip>
                </span>
              }
              key="controlled_fit_prev"
            >
              <Form.Item>
                <Input.Group>
                  {getControlled_fit_fields_prev(index).map((field) =>
                    generateFields('settings.team', field)
                  )}
                </Input.Group>
              </Form.Item>
            </TabPane>
          </Tabs>
        </Row>
      );
    }
    if (index !== 3) {
      return (
        <Row>
          <Tabs
            style={{ width: 530 }}
            defaultActiveKey={() => {
              let key = 'full_fit';
              if (
                recordToUpdate?.permissionManagement?.products_access[index]
              ) {
                const management =
                  recordToUpdate?.permissionManagement?.products_access[index]
                    ?.advancedManagement;
                if (management) {
                  if (management.blocked_fit.selected) key = 'blocked_fit';
                  if (management.controlled_fit.selected)
                    key = 'controlled_fit';
                }
              }
              return key;
            }}
            onChange={(activeKey) => handleMenuPopTabChange(activeKey, index)}
            destroyInactiveTabPane
          >
            <TabPane
              tab={
                <span>
                  {t('settings.team.full_fit')}
                  <Tooltip title={t('settings.team.tooltip.tooltip_1')}>
                    <InfoCircleOutlined
                      style={{ marginLeft: 6, fontSize: 16 }}
                    />
                  </Tooltip>
                </span>
              }
              key="full_fit"
            >
              {t('settings.team.no_config')}
            </TabPane>
            <TabPane
              tab={
                <span>
                  {t('settings.team.blocked_fit')}
                  <Tooltip title={t('settings.team.tooltip.tooltip_2')}>
                    <InfoCircleOutlined
                      style={{ marginLeft: 6, fontSize: 16 }}
                    />
                  </Tooltip>
                </span>
              }
              key="blocked_fit"
            >
              <Form.Item noStyle>
                <Input.Group>
                  {getBlocked_fit_fields(index).map((field) =>
                    generateFields('settings.team', field)
                  )}
                </Input.Group>
              </Form.Item>
            </TabPane>
            <TabPane
              tab={
                <span>
                  {t('settings.team.controlled_fit')}
                  <Tooltip title={t('settings.team.tooltip.tooltip_3')}>
                    <InfoCircleOutlined
                      style={{ marginLeft: 6, fontSize: 16 }}
                    />
                  </Tooltip>
                </span>
              }
              key="controlled_fit"
            >
              <Form.Item>
                <Input.Group>
                  {getControlled_fit_fields(index).map((field) =>
                    generateFields('settings.team', field)
                  )}
                </Input.Group>
              </Form.Item>
            </TabPane>
          </Tabs>
        </Row>
      );
    }
    return false;
  };

  const updateUser = async (body, userId) => {
    try {
      await dispatchAPI('PATCH', {
        url: `/users/${userId}`,
        body
      });
      antMsg.success(t('settings.team.success.updateUser'));
      setForceRefresh(!forceRefresh);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const updateInsurer = async (id, email) => {
    try {
      await dispatchAPI('PATCH', {
        url: `/insurers/associates/${insurer && insurer._id}`,
        body: {
          email
        }
      });
      antMsg.success(t('settings.team.success.updateInsurer'));
      setForceRefresh(!forceRefresh);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const createUser = async (body) => {
    try {
      await dispatchAPI('POST', {
        url: `/users`,
        body
      });
      updateInsurer(insurer._id, body.email);
      antMsg.success(t('settings.team.success.createUser'));
      setForceRefresh(!forceRefresh);
    } catch (e) {
      if (e?.response?.status === 409) {
        antMsg.error(t('login.email_in_db'));
      } else {
        message(e.response.status);
      }
    }
  };

  const handleSubmit = async (values) => {
    setSaveLoading(true);
    const body = {
      ...values,
      role: 'users:USER',
      insurer: user?.insurer?._id,
      refChef: user?._id
    };
    if (recordToUpdate) await updateUser(body, recordToUpdate.key);
    else await createUser(body);
    setSaveLoading(false);
    onClose();
  };

  useEffect(() => {
    if (isDrawerVisible) {
      if (recordToUpdate) form.setFieldsValue(recordToUpdate);
      else form.resetFields();
    }
    // eslint-disable-next-line
  }, [isDrawerVisible, recordToUpdate]);

  const getPopupText = (name) =>
    form.getFieldValue(['permissionManagement', name])
      ? `${t('settings.team.InfoText1')} ${t(`settings.team.form.${name}`)}`
      : `${t('settings.team.InfoText2')} ${t(`settings.team.form.${name}`)}`;

  return (
    <Drawer
      className="user-profile-drawer"
      title={t('settings.team.createProfile')}
      onClose={onClose}
      open={isDrawerVisible}
      size={430}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          permissionManagement: {
            dashboard: true,
            accounting: true,
            commercial_messages: true,
            remuneration: true,
            sinister: true,
            quotations: true,
            contracts: true
          }
        }}
      >
        {getFields(otherPositionSelected, setOtherPositionSelected).map(
          (field) => {
            if (recordToUpdate) {
              // eslint-disable-next-line no-param-reassign
              field.initialValue = recordToUpdate[field.name];
            }
            return generateFields('settings.team', field);
          }
        )}
        <Divider orientation="left" style={{ margin: '28px 0 12px 0' }}>
          {t('settings.team.permissionManagement.title')}
        </Divider>
        {menuPermissions.map((menu, key) => (
          <List
            key={['permissionManagement', menu]}
            header={
              key === 0
                ? t('settings.team.featureMenu')
                : t('settings.team.menu2')
            }
            grid={{ gutter: 8, column: 2 }}
            dataSource={menu}
            renderItem={(item) => (
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Row
                    gutter={8}
                    style={{ marginBottom: 10, padding: '0 4px' }}
                  >
                    <Col span={16}>{t(`settings.team.form.${item}`)}</Col>
                    <Col span={7}>
                      <Form.Item
                        noStyle
                        name={['permissionManagement', item]}
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    {['quotations', 'contracts'].includes(item) && (
                      <div
                        style={{
                          display: 'flex',
                          flexFlow: 'column',
                          marginLeft: 5,
                          marginTop: 0
                        }}
                      >
                        <span style={{ fontSize: 12, color: '#bbb' }}>
                          {getPopupText(item)}
                        </span>
                      </div>
                    )}
                  </Row>
                )}
              </Form.Item>
            )}
          />
        ))}
        <List
          key="3"
          header={t('settings.team.permissionManagement.products')}
          grid={{ gutter: 16, column: 1 }}
          dataSource={products}
          renderItem={(product, index) => (
            <Row style={{ padding: '0 8px', marginBottom: 24 }}>
              <Form.Item
                noStyle
                hidden
                name={[
                  'permissionManagement',
                  'products_access',
                  index,
                  'product'
                ]}
                initialValue={product._id}
              >
                <Input />
              </Form.Item>
              {[
                'full_fit',
                'blocked_fit',
                'blocked_fit_prev',
                'controlled_fit',
                'controlled_fit_prev'
              ].map((el) => (
                <Form.Item
                  key={el}
                  hidden
                  name={[
                    'permissionManagement',
                    'products_access',
                    index,
                    'advancedManagement',
                    el,
                    'selected'
                  ]}
                  valuePropName="checked"
                  initialValue={el === 'full_fit'}
                >
                  <Switch />
                </Form.Item>
              ))}
              <Col span={8}>{product?.name}</Col>
              <Col span={4}>
                <Form.Item
                  noStyle
                  name={[
                    'permissionManagement',
                    'products_access',
                    index,
                    'permitted'
                  ]}
                  valuePropName="checked"
                  initialValue
                >
                  <Switch style={{ marginLeft: 0 }} />
                </Form.Item>
              </Col>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) =>
                  getFieldValue([
                    'permissionManagement',
                    'products_access',
                    index,
                    'permitted'
                  ])
                    ? advancedManagement(index)
                    : ''
                }
              </Form.Item>
            </Row>
          )}
        />
        <Row justify="space-between" style={{ marginTop: 28 }}>
          <Button type="add" htmlType="submit">
            {t('buttons.save')}
            <CheckOutlined />
            <Spin spinning={saveLoading} style={{ marginLeft: 10 }} />
          </Button>
          <Button type="link" danger onClick={onClose}>
            {`${t('buttons.cancel')}`}
            <CloseOutlined />
          </Button>
        </Row>
      </Form>
    </Drawer>
  );
};

NewUserProfileDrawer.propTypes = {
  isDrawerVisible: PropTypes.bool.isRequired,
  setIsDrawerVisible: PropTypes.func.isRequired,
  setForceRefresh: PropTypes.func.isRequired,
  forceRefresh: PropTypes.bool.isRequired,
  recordToUpdate: PropTypes.shape({
    key: PropTypes.string,
    position: PropTypes.shape({
      value: PropTypes.arrayOf(PropTypes.string)
    }),
    permissionManagement: PropTypes.shape({
      dashboard: PropTypes.bool,
      remuneration: PropTypes.bool,
      accounting: PropTypes.bool,
      sinister: PropTypes.bool,
      quotations: PropTypes.bool,
      contracts: PropTypes.bool,
      commercial_messages: PropTypes.bool,
      products_access: PropTypes.arrayOf(
        PropTypes.shape({
          advancedManagement: PropTypes.shape({
            value: PropTypes.string,
            params: PropTypes.shape({})
          })
        })
      )
    })
  }),
  setRecordToUpdate: PropTypes.func.isRequired,
  insurer: PropTypes.shape({
    _id: PropTypes.string
  })
};

NewUserProfileDrawer.defaultProps = {
  recordToUpdate: undefined,
  insurer: undefined
};

export default NewUserProfileDrawer;
